/* eslint-disable no-unused-vars */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { apiClient } from "@finitless/shared/lib";
import { mapMenuImportError } from "@/lib/menuImportErrors";

// Backend for menu import (DoorDash, Uber Eats, etc.)
const MENU_IMPORT_API_URL = import.meta.env.VITE_MENU_IMPORT_API_URL || "";

/**
 * Generic hook to trigger menu import from delivery platforms.
 * Supports DoorDash, Uber Eats, Grubhub, etc. via source param.
 *
 * @param {string} source - Importer id: "doordash" | "ubereats" | "ordering" | etc.
 * @returns {Object} { triggerImport, triggerImportAsync, isImporting, error }
 */
export const useMenuImport = (source = "doordash") => {
  const { t } = useTranslation("menuImporters");
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params) => {
      const baseUrl = (MENU_IMPORT_API_URL || "").trim().replace(/\/$/, "");
      if (!baseUrl) {
        throw new Error(t("status.apiNotConfigured"));
      }
      const path = `/api/import/${source}`;
      const url = new URL(`${baseUrl}${path}`);

      if (params.useFixture) {
        url.searchParams.set("useFixture", "true");
      }

      const headers = { "Content-Type": "application/json" };
      const token = apiClient.getAuthToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const { useFixture: _uf, ...body } = params;
      Object.assign(body, { source });

      const response = await fetch(url.toString(), {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = mapMenuImportError(
          source,
          data.detail || data.message || data.error,
          response.status,
          t,
        );
        throw new Error(message);
      }

      if (data.success === false) {
        const message = data.error || data.message || t("status.error");
        throw new Error(message);
      }

      return data;
    },
    onSuccess: (data, variables) => {
      const count = data.appliedCount ?? variables.businessIds?.length ?? 0;
      const message =
        count <= 1
          ? t("status.success")
          : t("status.successToStores", { count });
      showToast(message, "success");
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: ["businesses"] });
      queryClient.invalidateQueries({ queryKey: ["brand-businesses"] });
      queryClient.invalidateQueries({ queryKey: ["businesses-products"] });
      // Remove cached product/category data so /menu/products/:slug fetches fresh.
      // These hooks use refetchOnMount: false, so invalidate alone won't refetch on navigate.
      queryClient.removeQueries({ queryKey: ["business-by-slug"] });
      queryClient.removeQueries({ queryKey: ["category-products"] });
      queryClient.removeQueries({ queryKey: ["business-products-v2"] });
      queryClient.removeQueries({ queryKey: ["business-extras"] });
    },
    onError: (error) => {
      showToast(error.message || t("status.error"), "error");
    },
  });

  return {
    triggerImport: mutation.mutate,
    triggerImportAsync: mutation.mutateAsync,
    isImporting: mutation.isPending,
    error: mutation.error,
  };
};

export default useMenuImport;
